import { useEffect, useMemo } from 'react';
import { Button, InputGroup } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { Job } from 'src/models/jobs/job.model';
import { useClient } from 'src/ui/shared/hooks/client.hook';
import { Icon } from '../../../icon/Icon';
import { HookForm } from '../../HookForm';

type RelevantFields = Pick<
    Job,
    | 'currencyCode'
    | 'jobCurrencyRemunerationFrom'
    | 'jobCurrencyRemunerationTo'
    | 'remunerationType'
    | 'remunerationDisplay'
>;

export const RemunerationSummaryControl = (): React.ReactElement => {
    const client = useClient();
    const culture = useMemo(() => {
        switch (client.countryCode) {
            case 'AU':
                return 'en-AU';
            case 'CA':
                return 'en-CA';
            case 'GB':
                return 'en-GB';
            case 'US':
                return 'en-US';
            case 'ZA':
                return 'en-ZA';
            default:
                return 'en';
        }
    }, [client.countryCode]);

    const { watch, setValue, formState } = useFormContext<RelevantFields>();

    const [currency, from, to, type] = watch([
        'currencyCode',
        'jobCurrencyRemunerationFrom',
        'jobCurrencyRemunerationTo',
        'remunerationType',
    ]);

    const calcualatedSummary = useMemo(() => {
        if (type === 'Unpaid') {
            return 'Unpaid';
        }

        const intervalDisplay = type === 'Annual' ? 'Yearly' : type;

        if (!from && !to) {
            return '';
        }

        const intl = new Intl.NumberFormat(culture, {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
        });

        if (from && to) {
            return `${
                from === to ? intl.format(from) : intl.formatRange(from, to)
            } ${intervalDisplay}`;
        }

        if (from) {
            return `From ${intl.format(from)} ${intervalDisplay}`;
        }

        if (to) {
            return `Up to ${intl.format(to)} ${intervalDisplay}`;
        }

        return '';
    }, [culture, currency, from, to, type]);

    useEffect(() => {
        if (!formState.dirtyFields.remunerationDisplay) {
            setValue('remunerationDisplay', calcualatedSummary);
        }
    }, [calcualatedSummary, formState.dirtyFields.remunerationDisplay, setValue]);

    return (
        <HookForm.Input
            label="Remuneration summary"
            name="remunerationDisplay"
            helpText="This value is displayed to students"
            rules={{ required: true }}
            append={
                <InputGroup.Append>
                    <Button
                        title="Regenerate automatic summary"
                        variant="outline-input"
                        disabled={!formState.dirtyFields.remunerationDisplay}
                        onClick={() => setValue('remunerationDisplay', calcualatedSummary)}
                    >
                        <span className="text-dark">
                            <Icon icon="arrow-counterclockwise" />
                        </span>
                    </Button>
                </InputGroup.Append>
            }
        />
    );
};
